/**
 * KID data transformers: API/DB (content as string) ↔ UI/PDF (content as string | array | number | ChartData).
 * parseContent: normalise stringified JSON from backend. transformKidsDataToPdfElement: layout → PDF (preview).
 * transformElementsToKidDataModels: editor → save (stringify). transformKidDataModelsToElements: load → editor (parse).
 */

import type {
  KidDataResponse,
  KidLayoutLevel,
  KidLayoutTemplateModel,
  KidOverviewDataItem,
  UpdatedKidDataModel,
} from '../../../../../api/kids/types';
import {
  addCommasToNumbersAndRound,
  percentageToTwoDecimalPlaces,
} from '../../../../../utilities/numberFormatters';
import {
  KidDocumentStatus,
  KidFund,
  KidFundRecord,
  KidShareClass,
  KidShareClassRecord,
} from '../types/KidEditorV2.types';
import type {
  LayoutConfig,
  PdfElement,
} from '../types/KidPdfGeneratorV2.types';

/** Backend sometimes sends content as stringified JSON (e.g. table data); parse to array/object or return as-is. */
export function parseContent(
  content: string | unknown,
): string | string[][] | number[][] | unknown[] | unknown {
  if (content === undefined || content === null) return content;
  if (typeof content !== 'string') return content;
  const trimmed = content.trim();
  if (trimmed === '') return content;
  if (
    (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
    (trimmed.startsWith('{') && trimmed.endsWith('}'))
  ) {
    try {
      return JSON.parse(trimmed) as string[][] | unknown[];
    } catch {
      return content;
    }
  }
  return content;
}

export const formatCurrency = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(Number(value))) return '';
  return `EUR ${value}`;
};

export const formatPercentPerYear = (
  value: number | null | undefined,
): string => {
  if (value == null || Number.isNaN(Number(value))) return '';
  const pct = (value * 100).toFixed(2);
  return `${pct}% (each year)`;
};

export const buildPerformanceScenarioTable = (
  scenarioData: any,
): string[][] => {
  if (!scenarioData || typeof scenarioData !== 'object') return [];

  const header: string[][] = [
    ['Scenarios', '', '1 Year', 'Holding period'],
  ];

  const scenariosOrder: Array<keyof typeof scenarioData> = [
    'stressed',
    'unfavourable',
    'moderate',
    'favourable',
  ];

  const labelMap: Record<string, string> = {
    stressed: 'Stress',
    unfavourable: 'Unfavourable',
    moderate: 'Moderate',
    favourable: 'Favourable',
  };

  const rows: string[][] = [];

  scenariosOrder.forEach((key) => {
    const scenario = scenarioData[key];
    if (!scenario) return;

    const oneYear = scenario['1yr_period'] || {};
    const holding = scenario['holding_period'] || {};

    rows.push([
      labelMap[key as string] || (key as string),
      'What you might get back after costs',
      oneYear.value != null ? addCommasToNumbersAndRound(oneYear.value) : '',
      holding.value != null ? addCommasToNumbersAndRound(holding.value) : '',
    ]);

    rows.push([
      '',
      'Average return each year',
      oneYear.return_value != null
        ? percentageToTwoDecimalPlaces(oneYear.return_value)
        : '',
      holding.return_value != null
        ? percentageToTwoDecimalPlaces(holding.return_value)
        : '',
    ]);
  });

  return header.concat(rows);
};

export const buildRiyTable = (riyData: any): string[][] => {
  if (!riyData || typeof riyData !== 'object') return [];

  const header: string[][] = [
    [
      'Investment Scenarios EUR 10,000',
      'If you cash in after 1 year',
      'If you exit after 3 years',
    ],
  ];
  const rows: string[][] = [];

  const oneYear = riyData['1yr_period'] || {};
  const holding = riyData['holding_period'] || {};

  rows.push([
    'Total costs',
    formatCurrency(oneYear.total_cost),
    formatCurrency(holding.total_cost),
  ]);

  rows.push([
    'Reduction in Yield (RIY) per year*',
    formatPercentPerYear(oneYear.riy),
    formatPercentPerYear(holding.riy),
  ]);

  return header.concat(rows);
};

export const buildCostsTable = (costsData: any): string[][] => {
  if (!costsData || typeof costsData !== 'object') return [];

  const header: string[][] = [
    ['This table shows the impact on return per year', '', '', ''],
  ];
  const rows: string[][] = [];

  const entryCost = costsData.entry_cost;
  const exitCost = costsData.exit_cost;
  const transactionCost = costsData.transaction_cost;
  const ongoingCost = costsData.ongoing_cost;
  const performanceFee =
    costsData.applied_performance_fee ?? costsData.notional_performance_fee;

  rows.push([
    'One-Off Costs',
    'Entry costs',
    entryCost != null ? `${entryCost}%` : '',
    'The impact of the costs you pay when entering your investment.',
  ]);

  rows.push([
    '',
    'Exit costs',
    exitCost != null ? `${exitCost}%` : '',
    'The maximum impact of the costs of exiting your investment when it matures.',
  ]);

  rows.push([
    'Ongoing Costs',
    'Transaction costs',
    transactionCost != null ? `${transactionCost}%` : '',
    'The impact of the costs of us buying and selling underlying investments for the product.',
  ]);

  rows.push([
    '',
    'Management fees and other administrative and operating costs',
    ongoingCost != null ? `${ongoingCost}%` : '',
    'The impact of the costs that we take each year for managing your investments.',
  ]);

  rows.push([
    'Incidental Costs',
    'Performance fees',
    performanceFee != null ? `${performanceFee}%` : '',
    'The impact of the performance fee. We take these from your investment if the product outperforms its high water mark, of (a) the highest previous net asset value per share of the series concerned at the end of a performance period; and (b) the initial issue price of the shares of that series.',
  ]);

  return header.concat(rows);
};

export type FiguresData = {
  scenario_data?: any;
  riy_data?: any;
  sri?: any;
  costs?: any;
} | null;

export const injectLatestFiguresIntoElements = (
  elements: any[],
  figuresData: FiguresData,
) => {
  if (!figuresData) return elements;

  return elements.map((element) => {
    switch (element.tag) {
      case 'performance_scenarios_table': {
        const table = buildPerformanceScenarioTable(figuresData.scenario_data);
        return {
          ...element,
          content: table.length > 0 ? table : element.content,
        };
      }
      case 'composition_of_costs_table': {
        const table = buildRiyTable(figuresData.riy_data);
        return {
          ...element,
          content: table.length > 0 ? table : element.content,
        };
      }
      case 'costs_over_time_table': {
        const table = buildCostsTable(figuresData.costs);
        return {
          ...element,
          content: table.length > 0 ? table : element.content,
        };
      }
      case 'sri_chart':
      case 'sri':
        return {
          ...element,
          content:
            figuresData.sri != null
              ? parseInt(figuresData.sri, 10)
              : element.content,
          rendered_content:
            figuresData.sri != null
              ? parseInt(figuresData.sri, 10)
              : element.content,
        };
      default:
        return element;
    }
  });
};

/** Layout styles from API → PdfElement for PDF preview (no editor). */
export function transformKidsDataToPdfElement(
  kidsData: KidDataResponse,
  index: number,
): PdfElement {
  const content = parseContent(kidsData.content);

  return {
    fieldId: kidsData.field_id || `field-${index}`,
    tag: kidsData.tag,
    content: content as PdfElement['content'],
    format_options: kidsData.format_options as PdfElement['format_options'],
    layout: kidsData.layout as unknown as PdfElement['layout'],
    template_id: kidsData.template_id,
  };
}

/** Editor PdfElement[] → UpdatedKidDataModel[] for save; all content stringified. */
function stringifyElementContent(content: PdfElement['content']): string {
  if (typeof content === 'string') {
    return content;
  }
  if (typeof content === 'number') {
    return content.toString();
  }
  return JSON.stringify(content);
}

export function transformElementsToKidDataModels(
  elements: PdfElement[],
  clientId: string,
  fundId: string,
  shareClassId: string,
  kidId: string,
  lastUpdatedBy: string,
  kidIdCreateDate: string,
): UpdatedKidDataModel[] {
  const currentYear = new Date().getFullYear();
  const currentTimestamp = new Date().toISOString();

  return elements.map((element, index) => {
    const contentString = stringifyElementContent(element.content);

    const formatOptionsWithLayout: Record<string, unknown> = {
      ...(element.format_options as Record<string, unknown> | undefined),
    };
    if (element.layout) {
      formatOptionsWithLayout.layout = element.layout;
    }

    return {
      kid_create_year: currentYear,
      client_id: clientId,
      fund_id: fundId,
      share_class_id: shareClassId,
      kid_id: kidId,
      kid_index: index,
      template_id: element.template_id,
      tag: element.tag,
      content: contentString,
      rendered_content:
        typeof element.content === 'string' ? element.content : undefined,
      format_options:
        Object.keys(formatOptionsWithLayout).length > 0
          ? formatOptionsWithLayout
          : undefined,
      last_updating_timestamp: currentTimestamp,
      last_updated_by: lastUpdatedBy,
      kid_id_create_date: kidIdCreateDate,
      field_id: element.fieldId,
      existing_markers: element.existing_markers || [],
    };
  });
}

/**
 * Editor PdfElement[] -> KidLayoutTemplateModel[] for layout-template save.
 * This differs from normal KID save because each row must carry layout metadata
 * like `kid_layout_id`, `kid_layout_level`, `kid_layout_value`, name and language.
 */
export function transformElementsToKidLayoutTemplateModels(
  elements: PdfElement[],
  params: {
    clientId: string;
    kidLayoutId: string;
    kidLayoutLevel: KidLayoutLevel;
    kidLayoutValue: string;
    kidLayoutName: string;
    documentLanguage: string;
    lastUpdatedBy: string;
  },
): KidLayoutTemplateModel[] {
  const currentYear = new Date().getFullYear();
  const currentTimestamp = new Date().toISOString();

  return elements.map((element, index) => {
    const hasTemplateId = Boolean(element.template_id);

    return {
      kid_layout_last_updated_year: currentYear,
      kid_layout_id: params.kidLayoutId,
      kid_index: index,
      kid_layout_level: params.kidLayoutLevel,
      kid_layout_value: params.kidLayoutValue,
      client_id: params.clientId,
      template_id: element.template_id ?? null,
      tag: element.tag,
      content: hasTemplateId ? null : stringifyElementContent(element.content),
      format_options:
        (element.format_options as Record<string, any> | undefined) ?? null,
      last_updating_timestamp: currentTimestamp,
      last_updated_by: params.lastUpdatedBy,
      field_id: element.fieldId,
      layout: (element.layout as Record<string, any> | undefined) ?? null,
      kid_layout_name: params.kidLayoutName,
      document_language: params.documentLanguage,
      existing_markers: element.existing_markers || [],
      rendered_content:
        !hasTemplateId && typeof element.content === 'string'
          ? element.content
          : null,
    };
  });
}

/** API UpdatedKidDataModel[] (content string) → PdfElement[] for editor; parse JSON content. */
export function transformKidDataModelsToElements(
  kidDataModels: UpdatedKidDataModel[],
): any[] {
  return kidDataModels
    .sort((a, b) => a.kid_index - b.kid_index)
    .map((model) => {
      let parsedContent: PdfElement['content'] = '';

      if (!model.content) {
        parsedContent = '';
      } else if (typeof model.content !== 'string') {
        parsedContent = model.content as PdfElement['content'];
      } else {
        const trimmedContent = model.content.trim();
        if (trimmedContent.startsWith('[') || trimmedContent.startsWith('{')) {
          try {
            parsedContent = JSON.parse(model.content) as PdfElement['content'];
          } catch {
            parsedContent = model.content;
          }
        } else {
          parsedContent = model.content;
        }
      }

      let finalContent: PdfElement['content'] = parsedContent;
      if (model.tag === 'sri_chart') {
        // Normalise SRI to a numeric value whenever possible
        if (typeof parsedContent === 'number') {
          finalContent = parsedContent;
        } else if (typeof parsedContent === 'string') {
          const numeric = Number(parsedContent);
          finalContent = Number.isNaN(numeric) ? parsedContent : numeric;
        } else {
          const contentObj = parsedContent as unknown as Record<
            string,
            unknown
          >;
          const sriValue =
            contentObj.SRI !== undefined
              ? (contentObj.SRI as number)
              : contentObj.sri !== undefined
                ? (contentObj.sri as number)
                : undefined;
          if (sriValue !== undefined) {
            finalContent = sriValue;
          }
        }
      }

      const modelWithLayout = model as Record<string, unknown>;
      const formatOptionsRaw = model.format_options;
      const formatOptions =
        typeof formatOptionsRaw === 'string'
          ? (() => {
              try {
                return JSON.parse(formatOptionsRaw) as Record<string, unknown>;
              } catch {
                return undefined;
              }
            })()
          : (formatOptionsRaw as Record<string, unknown> | undefined);

      let layout: LayoutConfig | undefined =
        (modelWithLayout.layout as LayoutConfig | undefined) ??
        (formatOptions?.layout as LayoutConfig | undefined);
      let cleanedFormatOptions: PdfElement['format_options'] = undefined;

      if (formatOptions) {
        if (formatOptions.layout) {
          const { layout: _layout, ...rest } = formatOptions;
          cleanedFormatOptions =
            Object.keys(rest).length > 0
              ? (rest as PdfElement['format_options'])
              : undefined;
        } else {
          cleanedFormatOptions =
            Object.keys(formatOptions).length > 0
              ? (formatOptions as PdfElement['format_options'])
              : undefined;
        }
      }

      return {
        id: model.kid_id,
        fieldId: model.field_id,
        field_id: model.field_id, // for comparison - Field(Alias causing naming issues in comparison)
        tag: model.tag === 'sri' ? 'sri_chart' : model.tag,
        content: finalContent,
        existing_markers: model.existing_markers,
        template_id: model.template_id,
        rendered_content: model.rendered_content,
        format_options: cleanedFormatOptions,
        layout,
      };
    });
}

// Transform flat API response to grouped by fund structure
export const transformApiDataToFunds = (
  items: KidOverviewDataItem[] | undefined,
): KidFundRecord[] => {
  if (!items) return [];

  const fundMap = new Map<string, KidFundRecord>();

  items.forEach((item) => {
    if (!fundMap.has(item.fund_id)) {
      fundMap.set(item.fund_id, {
        fund_id: item.fund_id,
        fund_name: item.fund_id, // API doesn't provide fund_name, using fund_id as fallback
        fund_name_long: item.fund_id,
        share_classes: [],
      });
    }

    const fund = fundMap.get(item.fund_id)!;
    // If share class is not active, set status to deactivated
    const status: KidDocumentStatus = item.kid_is_active
      ? (item.status as KidDocumentStatus)
      : KidDocumentStatus.Deactivated;
    const shareClass: KidShareClassRecord = {
      share_class_id: item.share_class_id,
      share_class_isin: '', // Not provided in API response
      version_number: item.version_number,
      status,
      kid_is_active: item.kid_is_active,
      document_language: item.document_language,
      description: item.description,
    };

    fund.share_classes.push(shareClass);
  });

  return Array.from(fundMap.values());
};

// Transform API data to internal Fund format
export const transformFunds = (
  apiFunds: KidFundRecord[] | undefined,
): KidFund[] => {
  if (!apiFunds) return [];

  return apiFunds.map((apiFund) => {
    // Map API share classes to KidShareClass, deriving status from kid_is_active
    const mappedShareClasses: KidShareClass[] = apiFund.share_classes.map(
      (apiSc) => ({
        share_class_id: apiSc.share_class_id,
        share_class_isin: apiSc.share_class_isin,
        document_language: apiSc.document_language,
        version_number: apiSc.version_number,
        status: apiSc.status,
        kid_is_active: apiSc.kid_is_active,
        // Derive alert flag directly from status instead of external map
        has_alert: apiSc.status === KidDocumentStatus.FiguresAlert,
        description: apiSc.description,
      }),
    );

    const editingCount = mappedShareClasses.filter(
      (sc) =>
        sc.status === KidDocumentStatus.Editing ||
        sc.status === KidDocumentStatus.Rejected,
    ).length;

    const readyForReviewCount = mappedShareClasses.filter(
      (sc) => sc.status === KidDocumentStatus.Edited,
    ).length;

    const readyToPublishCount = mappedShareClasses.filter(
      (sc) => sc.status === KidDocumentStatus.Reviewed,
    ).length;

    const publishedCount = mappedShareClasses.filter(
      (sc) => sc.status === KidDocumentStatus.Published,
    ).length;

    const hasReadyToPublish = readyToPublishCount > 0;
    const hasPublished = publishedCount > 0;
    const hasEditing = editingCount > 0;

    let editStatus: 'editing' | 'published' | 'no-edits' = 'no-edits';
    if (hasEditing) editStatus = 'editing';
    else if (hasPublished && !hasReadyToPublish) editStatus = 'published';

    // Calculate review and publish status labels with counts
    // Review status should only show when share classes are 'edited' (ready-for-review)
    const reviewStatus =
      readyForReviewCount > 0
        ? `In Review (${readyForReviewCount})`
        : 'None to Review';

    const activeShareClasses = mappedShareClasses.filter(
      (sc) => sc.kid_is_active,
    );

    return {
      fund_id: apiFund.fund_id,
      fund_name: apiFund.fund_name,
      fund_name_full: apiFund.fund_name,
      shareClasses: mappedShareClasses,
      editStatus,
      reviewStatus,
      publishedStatus: hasReadyToPublish
        ? `Ready to Publish (${readyToPublishCount})`
        : 'Published',
      totalShareClasses: apiFund.share_classes.length,
      completedShareClasses: activeShareClasses.length,
    };
  });
};
